// src/../../Graphs/MonthlyRevenueChart.tsx
import React, { JSX } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  CartesianGrid,
} from "recharts"; // Changed LineChart and Line to BarChart and Bar
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { ChartConfig, ChartTooltipContent } from "@/components/ui/chart";
import { ChartTooltip } from "@/components/ui/chart";
import { ChartContainer } from "@/components/ui/chart";
import { Button } from "@/components/ui/button";

interface MonthlyRevenueChartProps {
  loading: boolean;
  monthlyRevenue: {month: number, revenue: string, year: number }[]; // Array of months/revenue for the last 12 months
}

interface MonthlyData {
  month: string;
  revenue: number;
}

/**
 * MonthlyRevenueChart component displays a bar chart of monthly revenue data.
 * If the data is loading, it shows a skeleton loader with random heights.
 * If there is no data, it displays a "No data available" message.
 *
 * @component
 * @param {MonthlyRevenueChartProps} props - The props for the component.
 * @param {boolean} props.loading - Indicates if the data is currently loading.
 * @param {Array<{ month: number, year: number, revenue: number }>} props.monthlyRevenue - The monthly revenue data.
 *
 * @returns {JSX.Element} The rendered MonthlyRevenueChart component.
 *
 * @example
 * <MonthlyRevenueChart loading={false} monthlyRevenue={[{ month: 1, year: 2023, revenue: 1000 }]} />
 */
const MonthlyRevenueChart: React.FC<MonthlyRevenueChartProps> = ({ loading, monthlyRevenue }: MonthlyRevenueChartProps): JSX.Element => {

  // Hooks
  const { t } = useTranslation(["components", "common"]);

  const monthsMapping = {
    1: t("common:monthsshort.january"),
    2: t("common:monthsshort.february"),
    3: t("common:monthsshort.march"),
    4: t("common:monthsshort.april"),
    5: t("common:monthsshort.may"),
    6: t("common:monthsshort.june"),
    7: t("common:monthsshort.july"),
    8: t("common:monthsshort.august"),
    9: t("common:monthsshort.september"),
    10: t("common:monthsshort.october"),
    11: t("common:monthsshort.november"),
    12: t("common:monthsshort.december")
  };

  const chartConfig = {
    revenue: {
      label: t("monthlyrevenuechart.revenue"),
    },
  } satisfies ChartConfig;

  // Function to generate month labels and pair them with revenue data
  const generateMonthlyData = (): MonthlyData[] => {
    return monthlyRevenue.map((data) => {
      return {
        month: `${monthsMapping[data.month]} ${data.year}`,
        revenue: Number(data.revenue)
      };
    });
  };

  const chartData = generateMonthlyData();

  const hasData = chartData.map((d) => d.revenue).some((r) => r > 0);

  if(loading) {
    // Generate an array of 10 random heights between 20% and 100%
    const bars = Array.from({ length: 10 }, () => Math.floor(Math.random() * 80) + 20);

    return (
      <div className="w-full h-80 p-4 flex justify-between items-end gap-2">
        {bars.map((height, index) => (
          <Skeleton
            key={index}
            style={{ height: `${height}%` }}
            className="w-4 bg-gray-300 rounded"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="grow-1 w-full max-h-[300px] lg:max-h-none lg:h-[calc(50vh-8rem)] p-2">
      <div className="w-full text-center flex items-center">
        <h1 className="font-semibold justify-center flex-1">
          {t("monthlyrevenuechart.title")}
        </h1>
        <div className="flex justify-end">
          <HoverCard>
            <HoverCardTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 p-0 hover:cursor-help"
              >
                <InformationCircleIcon className="h-5 w-5" />
              </Button>
            </HoverCardTrigger>
            <HoverCardContent className="w-80 text-sm font-normal">
              {t("monthlyrevenuechart.tooltip")}
            </HoverCardContent>
          </HoverCard>
        </div>
      </div>
      {
        !hasData ? (
          <div className="w-full text-center lg:-mt-6 h-full justify-center items-center flex">
            <p className="text-sm text-slate-500 pb-4">{t("common:nodataavailable")}</p>
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="w-full h-full pb-4">
            <BarChart accessibilityLayer data={chartData} barSize={30}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="month"
                tickLine={false}
                tickMargin={3}
                axisLine={false}
              />
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent currency />}
              />
              <Bar dataKey="revenue" fill="#6B9CFF" radius={8} />
            </BarChart>
          </ChartContainer>
        )
      }
    </div>
  );
};

export default MonthlyRevenueChart;
